import React, { useState, useEffect, useMemo } from "react";

import Box from '@mui/material/Box';

import "../App.css"
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {PageHeader} from "./PageHeader";
import {CxComboxSelection} from "../cmx/CxForm";
import {CxText} from "../cmx/CxTypography"
import {CxGrid} from "../cmx/CxGrid";
import {CxDataGrid} from "../cmx/CxData";
import {Code} from "../domain/Code";


export function CodeList({type}) {

    const {handleAction, getPageState, setPageState} = useAppCtx();
    const {code, loadCodes, codeLoadCount} = useCodeCtx();

    useEffect(() => {
        loadCodes();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const pageKey  = 'CodeList';
    const taskCode = getPageState(pageKey, 'taskCode');

    const typeCfg = {
        Category : {
            viewKey         : 'catView',
            label           : "Arbeitskategorien",              // einzahl/merhzahl einfürhen
            singularLabel   : "Arbeitskategorie",
            code            : "task",
            isMainCode      : true,
            showRef         : false,
            showQuantity    : false,
            showWarranty    : false,
            showPump        : false,
            showMaterialList: false,
            showClearingList: false,
            returnToAction  : "codeCategory",
            isTask          : false,
        },
        Task : {
            viewKey         : 'taskView',
            label           : "Arbeitsvorgänge",
            singularLabel   : "Arbeitsvorgang",
            code            : taskCode,
            isMainCode      : false,
            showRef         : true,
            showQuantity    : false,
            showWarranty    : true,
            showPump        : true,
            showMaterialList: true,
            showClearingList: true,
            returnToAction  : "codeTask",
            isTask          : true
        },
        Tool : {
            viewKey         : 'toolView',
            label           : "Werkzeuge",
            singularLabel   : "Werkzeug",
            code            : "tool.0",
            isMainCode      : false,
            showRef         : false,
            showQuantity    : true,
            showWarranty    : false,
            showPump        : false,
            showMaterialList: false,
            showClearingList: false,
            returnToAction  : "codeTool",
            isTask          : false
        }
    }

    const cfg = typeCfg[type];

    const view = getPageState(pageKey, cfg.viewKey);
    const disableTask = cfg.isTask && code.getTaskSelection("", true).length === 0;

    const handleViewChange = (value) => {
        setPageState(pageKey, cfg.viewKey, value);
    };

    const viewItems = [
        { label: "alle",    value: "allView" },
        { label: "aktiv",   value: "activeView" },
        { label: "inaktiv", value: "inactiveView" },
    ];

    const actionItems = [
        { kind: "icon",    label: "Erstellen", iconName: "create", actionName: `codeCreate` },
        { kind: "icon",    label: "Sortieren", iconName: "sort",   actionName: `codeSort` },
    ];

    const handleLocalActionChange = (action) => {
        const data = {cfg: cfg, items:items};
        handleAction(action, data, cfg.returnToAction);
    };

    const renderHeader = () => {
        return (
           <>
               { disableTask  &&
                   <PageHeader key={cfg.viewKey} title={cfg.label} />
               }
               { !disableTask &&
                   <PageHeader
                       key={cfg.viewKey}
                       title={cfg.label}
                       viewItems={viewItems}
                       viewInitialValue={view}
                       viewOnChangeHandler={handleViewChange}
                       localActionItems={actionItems}
                       localActionOnChangeHandler={handleLocalActionChange} />
               }
           </>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const handleTaskChange = (task) => {
        setPageState('CodeList', 'taskCode', task);
    };

    const renderControlCategory = () => {
        return (
            <CxGrid size="xl">
                <CxText>Verwaltung Arbeitskategorien</CxText>
            </CxGrid>
        );
    };

    const renderControlTask = () => {
        return (
            <>
                { disableTask &&
                    <CxText>Bitte erfassen sie zuerst eine Arbeitskategorie</CxText>
                }

                { !disableTask && <>
                    <CxText>Verwaltung Arbeitsvorgänge für die gewählte Arbeitskategorie</CxText>
                    <CxGrid size="xl">
                        <CxGrid size="md">
                            <CxComboxSelection
                                id="task"
                                name="Arbeitskategorie"
                                value={taskCode}
                                onChange={handleTaskChange}
                                items={code.getTaskSelection("", true)}
                            />
                        </CxGrid>
                        <CxGrid size="md" />
                        <CxGrid size="lg" />
                    </CxGrid>
                </> }
            </>
        );
    }

    const renderControlTool = () => {
        return (
            <CxGrid size="xl">
                <CxText>Verwaltung Pumpen</CxText>
            </CxGrid>
        );
    };

    const renderControl = () => {
        switch (type) {
            case 'Category':
                return renderControlCategory();
            case 'Task':
                return renderControlTask();
            case 'Tool':
                return renderControlTool();
            default:
                return <div>Unknown Control Type</div>;
        }
    };


    // ----------------------------------------------------------------------------------------------------------------
    // filter view data from cache
    // ----------------------------------------------------------------------------------------------------------------

    const [items, setItems] = useState(null);

    // load the view data from cache
    useEffect(() => {
        if (cfg.isMainCode) {
            setItems(code.getMainCodes(cfg.code));
        } else {
            setItems(code.getSubCodes(cfg.code));
        }
    }, [type, taskCode, codeLoadCount]);

    // filter the items depending on the current items and view
    const rows = useMemo(() => {
        if (!items) return [];

        switch (view) {
            case 'activeView':
                return items.filter(item => item.active === 1);
            case 'inactiveView':
                return items.filter(item => item.active === 0);
            case 'allView':
            default:
                return items;
        }
    }, [items, view]);

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleEdit = (item) => {
        const data = {cfg: cfg, items:items, item:item};
        handleAction('codeUpdate', data, cfg.returnToAction);
    }

    const renderCodes = () => {
        return (
            <CxGrid size="xl">
                <CxDataGrid
                    rows={Code.getListValues(rows)}
                    columns={Code.getListColumns(cfg)}
                    onRowDoubleClick={undefined}
                    onViewAction={undefined}
                    onEditAction={(params) => handleEdit(params.row)}
                    getRowId={(row) => row.key}
                />
            </CxGrid>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader() }
                { renderControl() }
                { rows && renderCodes() }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.